/**
 * HourlyHeaderMobile — redesigned mobile header for HOURLY workers.
 *
 * A semicircular 6→18 shift dial (iOS Sleep-Schedule style) with the live
 * timer in its centre, clock in/out times beside it, and a production bar
 * with a red→green gradient + salary/goal markers. Ported from the
 * Claude-Design handoff (Turn 8). Three states: not started, on shift, done.
 */

import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { SignOut, SignIn, Check, UserCircle } from '@phosphor-icons/react';
import NotificationBell from './NotificationBell';

const BLUE = '#2563eb';

const pad2 = (n) => String(n).padStart(2, '0');
const fmtHM = (iso) => { const d = new Date(iso); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtElapsed = (s) => `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;

// Hour (6..18) → point on the r=70 semicircle centred at (90,100).
function hourToXY(h) {
  const hh = Math.max(6, Math.min(18, h));
  const th = (hh - 6) * 15 * Math.PI / 180;
  return { x: 90 - 70 * Math.cos(th), y: 100 - 70 * Math.sin(th) };
}
const hourOf = (iso) => { const d = new Date(iso); return d.getHours() + d.getMinutes() / 60; };

export default function HourlyHeaderMobile({
  user, clockEvent, elapsed, isClockedIn, isClockedOut, clockLoading,
  onClockIn, onClockOut, onLogout, onAccount,
}) {
  const [prod, setProd] = useState(null);

  useEffect(() => {
    const load = () => api.getMyProduction().then(setProd).catch(() => {});
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [elapsed && isClockedIn ? Math.floor(elapsed / 45) : 0]); // refresh ~45s while on shift

  const hasShift = isClockedIn || isClockedOut;
  const startH = hasShift && clockEvent?.clock_in_at ? hourOf(clockEvent.clock_in_at) : null;
  const endH   = isClockedOut && clockEvent?.clock_out_at
    ? hourOf(clockEvent.clock_out_at)
    : (startH != null ? Math.min(18, startH + 8) : null);

  const s = startH != null ? hourToXY(startH) : null;
  const e = endH   != null ? hourToXY(endH)   : null;
  const arcD = s && e ? `M${s.x.toFixed(1)},${s.y.toFixed(1)} A70,70 0 0 1 ${e.x.toFixed(1)},${e.y.toFixed(1)}` : null;

  const now = new Date();
  const lead = isClockedIn ? hourToXY(now.getHours() + now.getMinutes() / 60) : null;

  const centerLabel = isClockedIn ? 'ON SHIFT' : isClockedOut ? 'TOTAL' : 'SHIFT';
  const centerValue = isClockedIn
    ? fmtElapsed(elapsed || 0)
    : isClockedOut
      ? `${(clockEvent?.hours_worked || 0).toFixed(2)}h`
      : '—';

  // Clock in/out display
  const inTime  = clockEvent?.clock_in_at ? fmtHM(clockEvent.clock_in_at) : '—';
  const outTime = isClockedOut && clockEvent?.clock_out_at
    ? fmtHM(clockEvent.clock_out_at)
    : (clockEvent?.clock_in_at ? fmtHM(new Date(new Date(clockEvent.clock_in_at).getTime() + 8 * 3600000)) : '—');
  const outSub  = isClockedOut ? 'Today' : 'Expected';

  // Production
  const production = prod ? prod.production : 0;
  const goal = prod ? prod.goal : 0;
  const wage = prod ? prod.wage : 0;
  const jobs = prod ? prod.jobCount : 0;
  const pct  = goal > 0 ? Math.round((production / goal) * 100) : 0;
  const fillPct = Math.min(pct, 100);
  const salaryPct = goal > 0 ? Math.min((wage / goal) * 100, 100) : 62;
  const hitSalary = wage > 0 && production >= wage;
  const fillGradient = fillPct >= salaryPct
    ? 'linear-gradient(90deg,#e01818,#f59e0b,#16a34a)'
    : 'linear-gradient(90deg,#e01818,#f59e0b)';

  const iconBtn = { position: 'relative', background: '#f3f4f6', border: 'none', width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };

  return (
    <header className="mobile-hourly-header" style={{ background: '#fff', padding: '6px 16px 16px', borderBottom: '1px solid #eef0f2', position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <img src={process.env.PUBLIC_URL + '/ACTS_CAR_DETAILING.png'} alt="Acts" style={{ height: 46, objectFit: 'contain' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NotificationBell
            onAccount={onAccount}
            extra={!isClockedIn && !isClockedOut
              ? [{ id: 'clockin', kind: 'clockin', title: 'Clock in', body: "You haven't clocked in today yet.", actionLabel: 'Clock in', onAction: onClockIn }]
              : []}
          />
          <button onClick={onAccount} style={{ ...iconBtn, color: '#1e3a4a' }} title="My Account" aria-label="My Account">
            <UserCircle size={20} weight="duotone" />
          </button>
          <button onClick={onLogout} style={{ ...iconBtn, color: '#374151' }} title="Logout" aria-label="Logout">
            <SignOut size={19} />
          </button>
        </div>
      </div>

      {/* Dial + side info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <svg width="180" height="112" viewBox="0 0 180 112" style={{ flex: 'none' }}>
          {/* track + inner tick ring */}
          <path d="M20,100 A70,70 0 0 1 160,100" fill="none" stroke="#eceef0" strokeWidth="16" />
          <path d="M20,100 A70,70 0 0 1 160,100" fill="none" stroke="#f8f9fa" strokeWidth="10" strokeDasharray="1 3.6" />
          {/* shift arc */}
          {arcD && <>
            <path d={arcD} fill="none" stroke={BLUE} strokeWidth="16" strokeLinecap="round" />
            <path d={arcD} fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="11" strokeDasharray="1.4 4" />
            <circle cx={s.x} cy={s.y} r="8" fill={BLUE} /><circle cx={s.x} cy={s.y} r="2.6" fill="#fff" />
            <circle cx={e.x} cy={e.y} r="8" fill={BLUE} /><circle cx={e.x} cy={e.y} r="2.6" fill="#fff" />
          </>}
          {/* live current-time dot */}
          {lead && <g transform={`translate(${lead.x.toFixed(1)},${lead.y.toFixed(1)})`} style={{ animation: 'leadPulse 1.8s ease-in-out infinite' }}>
            <circle r="6.5" fill="#fff" /><circle r="4" fill={BLUE} />
          </g>}
          {/* hour numbers */}
          <text x="42" y="104" textAnchor="middle" style={{ font: '600 10px system-ui', fill: '#1a1a2e' }}>6</text>
          <text x="48.4" y="80" textAnchor="middle" style={{ font: '500 9px system-ui', fill: '#c0c6cc' }}>8</text>
          <text x="66" y="62.4" textAnchor="middle" style={{ font: '500 9px system-ui', fill: '#c0c6cc' }}>10</text>
          <text x="90" y="56" textAnchor="middle" style={{ font: '600 10px system-ui', fill: '#1a1a2e' }}>12</text>
          <text x="114" y="62.4" textAnchor="middle" style={{ font: '500 9px system-ui', fill: '#c0c6cc' }}>14</text>
          <text x="131.6" y="80" textAnchor="middle" style={{ font: '500 9px system-ui', fill: '#c0c6cc' }}>16</text>
          <text x="138" y="104" textAnchor="middle" style={{ font: '600 10px system-ui', fill: '#1a1a2e' }}>18</text>
          {/* centre readout */}
          <text x="90" y="82" textAnchor="middle" style={{ font: '700 7px system-ui', letterSpacing: '1px', fill: '#9ca3af' }}>{centerLabel}</text>
          <text x="90" y="100" textAnchor="middle" style={{ font: '600 17px system-ui', letterSpacing: '-.5px', fill: BLUE, fontVariantNumeric: 'tabular-nums' }}>{centerValue}</text>
        </svg>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: '#9ca3af' }}>
                <SignIn size={12} weight="fill" color={BLUE} />CLOCK IN
              </div>
              <div style={{ font: '700 19px system-ui', color: '#1a1a2e', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{inTime}</div>
              <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>Today</div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, font: '700 9px system-ui', letterSpacing: '.6px', color: isClockedOut ? '#15803d' : '#9ca3af' }}>
                <SignOut size={12} weight="fill" color={isClockedOut ? '#16a34a' : '#9ca3af'} />CLOCK OUT
              </div>
              <div style={{ font: '700 19px system-ui', color: isClockedOut ? '#16a34a' : '#9ca3af', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{outTime}</div>
              <div style={{ font: '400 11px system-ui', color: '#9ca3af' }}>{outSub}</div>
            </div>
          </div>

          {/* Action button */}
          {isClockedOut ? (
            <button disabled style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', color: '#9ca3af', height: 36, padding: '0 16px', borderRadius: 10, border: '1.5px solid #e5e7eb', font: '700 13px system-ui', cursor: 'default', whiteSpace: 'nowrap' }}>
              <Check size={14} />Done
            </button>
          ) : isClockedIn ? (
            <button onClick={onClockOut} disabled={clockLoading} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'transparent', color: '#e01818', height: 36, padding: '0 16px', borderRadius: 10, border: '1.5px solid #f3c6c6', font: '700 13px system-ui', cursor: clockLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 10 }}>■</span>{clockLoading ? 'GPS…' : 'Clock out'}
            </button>
          ) : (
            <button onClick={onClockIn} disabled={clockLoading} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#16a34a', color: '#fff', height: 36, padding: '0 18px', borderRadius: 10, border: 'none', font: '700 13px system-ui', cursor: clockLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
              <SignIn size={14} weight="fill" />{clockLoading ? 'GPS…' : 'Clock in'}
            </button>
          )}
        </div>
      </div>

      {/* Production bar */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', font: '600 11px system-ui', color: '#9ca3af', marginBottom: 6 }}>
          <span>Production</span>
          <span><b style={{ color: hitSalary ? '#16a34a' : '#c2410c' }}>{pct}%</b> of goal · {jobs} job{jobs === 1 ? '' : 's'}</span>
        </div>
        <div style={{ position: 'relative', height: 9, background: '#eceef0', borderRadius: 6 }}>
          <div style={{ width: `${fillPct}%`, height: '100%', background: fillGradient, borderRadius: 6, transition: 'width .5s ease' }} />
          <div style={{ position: 'absolute', left: `${salaryPct}%`, top: -3, bottom: -3, width: 2, background: '#1a1a2e', borderRadius: 2 }} title="Salary" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', font: '600 10px system-ui', color: '#9ca3af', marginTop: 4 }}>
          <span>$0</span><span>Salary</span><span style={{ color: '#16a34a' }}>Goal</span>
        </div>
      </div>

      <style>{`@keyframes leadPulse { 0%,100% { opacity: 1 } 50% { opacity: .55 } }`}</style>
    </header>
  );
}
